import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppStore } from "@/stores";
import { useLocation } from "wouter";
import useSWR from "swr";
import { TableType, deletePlan, getAll } from "@/lib/supabase";
import { Loader } from "@/components/Loader";
import { getRoute } from "@/lib/routes";
import { createPlansTableColumns, PlansTable } from "./components/plans-table";
import { ErrorWithRetry } from "@/components/ErrorWithRetry";

export function PlansPage() {
  const appState = useAppStore((state) => state);
  const { currentUser } = appState;

  const {
    data: plans,
    error,
    isLoading,
    mutate,
  } = useSWR(
    [
      "plans" as TableType,
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([table, companyId]) => getAll(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const [, setLocation] = useLocation();

  const plansTableColumns = createPlansTableColumns({
    goToPlan: (id: string) => setLocation(getRoute("plans", "plan", id)),
    onDeletePlan: async (id: string) => {
      await deletePlan(Number(id));
      const updatedPlans = plans?.filter((plan) => plan.id !== Number(id));
      mutate(updatedPlans);
    },
  });

  const isSuperAdmin = currentUser?.is_super_admin;
  const isProduction = import.meta.env.VITE_SITE_URL === "https://dashboard.revelate.app";

  if (isLoading || !plans) {
    return <Loader />;
  }
  if (error) {
    return (
      <ErrorWithRetry
        error={error.message}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <main className="flex-1 sm:py-0">
      <Card
        x-chunk="dashboard-05-chunk-3"
        className="relative border-transparent"
      >
        {/* Header */}
        <div className="flex flex-row justify-between items-center pt-3 pb-8">
          <CardHeader className="flex p-0">
            <CardTitle>
              <h1>
                Compensation Plans
              </h1>
            </CardTitle>
          </CardHeader>
          <div className="flex flex-row gap-x-2">
            {isSuperAdmin && !isProduction && (
              <Button
                onClick={() => setLocation(getRoute("plans", "plan", "build"))}
              >
                Build new plan
              </Button>
            )}

            <Button
              onClick={() => setLocation(getRoute("plans", "plan", "new"))}
            >
              Add new plan
            </Button>
          </div>
        </div>

        {/* Plans */}
        <CardContent className="px-0">
          {plans && plans.length > 0 ? (
            <PlansTable plans={plans} columns={plansTableColumns} />
          ) : (
            <div className="text-lg text-muted-foreground text-center flex flex-col justify-center h-[400px]">
              Add your first plan to start rewarding performance 🚀
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
